import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Quote, Star } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const TESTIMONIALS = [
  {
    name: "Aarav",
    grade: "Class 10",
    board: "CBSE Board",
    year: "2023-24",
    mode: "Home Tuitions",
    subject: "Mathematics & Science",
    review:
      "The two free 1-hour demo lectures gave my parents instant confidence. My math score jumped from 68 to 96! The teacher explained theorems using real-life geometry examples rather than rote formulas.",
  },
  {
    name: "Ananya",
    grade: "Class 12",
    board: "ISC Board",
    year: "2022-23",
    mode: "Personal 1-on-1",
    subject: "Physics & Chemistry",
    review:
      "When I needed to switch physics teachers due to a clash of timings, M² arranged an outstanding replacement within 24 hours. The 1-on-1 career counselling also helped me pick my engineering branch without confusion.",
  },
  {
    name: "Devansh",
    grade: "Class 9",
    board: "ICSE Board",
    year: "2023-24",
    mode: "Remote Digital Tuition",
    subject: "All Subjects",
    review:
      "We live in an interior township where top tutors weren't physically accessible. M² connected us with a brilliant young tutor online. Zero lag, clear notes, and weekly mock tests made all the difference.",
  },
  {
    name: "Riya",
    grade: "Class 11",
    board: "State Board (Maharashtra)",
    year: "2021-22",
    mode: "Small Group Batch",
    subject: "Biology & Chemistry",
    review:
      "M² Tuitions has been our family's trusted academic partner since 2019. Their teachers are both disciplined and incredibly warm. The concept-first approach helped me overcome my fear of organic chemistry.",
  },
  {
    name: "Kabir",
    grade: "Class 8",
    board: "Cambridge IGCSE",
    year: "2024",
    mode: "Personal Tuitions",
    subject: "Extended Math & Physics",
    review:
      "Finding an IGCSE-specific curriculum tutor locally was almost impossible. Cheshta ma'am matched us with a specialized instructor who knows the Cambridge syllabus inside out.",
  },
];

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = TESTIMONIALS.length;

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 5500);
    return () => clearInterval(t);
  }, [paused, total]);

  const t = TESTIMONIALS[idx];

  return (
    <section
      id="reviews"
      data-testid="testimonials-section"
      className="bg-white py-20 sm:py-24 lg:py-28"
    >
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal className="flex flex-col items-center text-center">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-navy">
            Student Feedback
          </span>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Real families. Real results.
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            className="relative mt-12 rounded-3xl border border-slate-200 bg-[#FAFBFD] p-8 shadow-[0_24px_60px_-30px_rgba(15,35,68,0.25)] sm:p-12"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <Quote className="h-10 w-10 rotate-180 text-gold" fill="currentColor" strokeWidth={0} />
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                data-testid="testimonial-quote"
                initial={{ opacity: 0, x: 36 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -36 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              >
                <p className="mt-5 min-h-[150px] font-serif text-lg italic leading-relaxed text-slate-800 sm:min-h-[130px] sm:text-2xl sm:leading-relaxed">
                  "{t.review}"
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <span className="font-serif text-lg font-bold text-navy-dark">
                    {t.name}
                  </span>
                  <span className="flex items-center gap-1 text-sm font-semibold text-gold">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
                    ))}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[t.grade, t.board, t.year, t.mode].map((chip) => (
                    <span
                      key={chip}
                      className="rounded-full border border-navy/15 bg-white px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-navy"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-8 flex items-center justify-between border-t border-slate-200 pt-6">
              <button
                data-testid="testimonial-prev-btn"
                aria-label="Previous testimonial"
                onClick={() => setIdx((idx - 1 + total) % total)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-navy-dark transition hover:bg-navy hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-2">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`testimonial-dot-${i}`}
                    aria-label={`Go to testimonial ${i + 1}`}
                    onClick={() => setIdx(i)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      i === idx ? "w-7 bg-navy" : "w-2 bg-slate-300 hover:bg-slate-400"
                    }`}
                  />
                ))}
              </div>
              <button
                data-testid="testimonial-next-btn"
                aria-label="Next testimonial"
                onClick={() => setIdx((idx + 1) % total)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-navy-dark transition hover:bg-navy hover:text-white"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
